import { Search, X } from "lucide-react";
import { Suspense, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { PanelLoading } from "~/components/loading";
import { Button, Input, Label } from "~/components/ui";
import { v2Route } from "~/v2/routes";
import { GeoItemsTable } from "./GeoItemsTable";

interface GeoItemsPresenterProps {
  label?: string;
  pageIndex: number;
  pageSize: number;
  isPending: boolean;
  onSearch: (label: string | undefined) => void;
  onPageIndexChange: (next: number) => void;
  onPageSizeChange: (next: number) => void;
}

export function GeoItemsPresenter({
  label,
  pageIndex,
  pageSize,
  isPending,
  onSearch,
  onPageIndexChange,
  onPageSizeChange,
}: GeoItemsPresenterProps) {
  const { t } = useTranslation();
  const [draftLabel, setDraftLabel] = useState(label ?? "");
  const previousLabel = useRef(label);
  if (previousLabel.current !== label) {
    previousLabel.current = label;
    setDraftLabel(label ?? "");
  }

  const submit = () => onSearch(draftLabel.trim() || undefined);
  const reset = () => {
    setDraftLabel("");
    onSearch(undefined);
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-dewey-title text-fg-1">{t("feature.geoItems.list.title")}</h1>
        <Button asChild>
          <Link to={v2Route.areaInput.create.$buildPath({})}>{t("feature.geoItems.list.create")}</Link>
        </Button>
      </div>

      <form
        className="flex max-w-xl items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label htmlFor="geo-item-label-search" className="text-12-bold text-fg-2">
            {t("feature.geoItems.list.label")}
          </Label>
          <Input
            id="geo-item-label-search"
            value={draftLabel}
            onChange={(event) => setDraftLabel(event.target.value)}
          />
        </div>
        <Button type="submit" size="sm">
          <Search />
          <span>{t("ui.search")}</span>
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={reset}>
          <X />
          <span>{t("ui.reset")}</span>
        </Button>
      </form>

      <Suspense key={label ?? ""} fallback={<PanelLoading />}>
        <GeoItemsTable
          label={label}
          pageIndex={pageIndex}
          pageSize={pageSize}
          isPending={isPending}
          onPageIndexChange={onPageIndexChange}
          onPageSizeChange={onPageSizeChange}
        />
      </Suspense>
    </div>
  );
}
